import { Link } from "react-router-dom";
import { ArrowRight, Shield, Heart, BookOpen, Users, Star } from "lucide-react";
import { MaskedLines, FadeUp, StaggerGroup, StaggerItem } from "@/components/Reveal";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/02b56810-04c0-441d-a60c-72a34e7b1677/images/7b85c0e8ea898261078b3632f6c6b5efcab5cc7b0721eb63ee6cb0362c4015ae.jpeg";
const BELONG_IMG = "/house.jpg";

const PILLARS = [
  {
    title: "Safety & Stability",
    copy: "A secure, consistent home with clear boundaries and trusted relationships.",
    Icon: Shield,
    circle: "#2B8A78",
    ring: "#2B8A78",
  },
  {
    title: "Emotional Wellbeing",
    copy: "Trauma-informed, caring support to build confidence, resilience and emotional regulation.",
    Icon: Heart,
    circle: "#E2574C",
    ring: "#F5A63B",
  },
  {
    title: "Education & Achievement",
    copy: "Support to attend and engage in education, with a focus on future opportunities.",
    Icon: BookOpen,
    circle: "#7C5FC9",
    ring: "#5CFFAF",
  },
];

const OUTCOMES = [
  { label: "Safety & stability", Icon: Shield, circle: "#2B8A78" },
  { label: "Emotional wellbeing", Icon: Heart, circle: "#E2574C" },
  { label: "Education & achievement", Icon: BookOpen, circle: "#7C5FC9" },
  { label: "Relationships & identity", Icon: Users, circle: "#2B8A78" },
  { label: "Independence", Icon: Star, circle: "#F5A63B" },
];

const SectionEyebrow = ({ children }) => (
  <p className="flex items-center gap-3 text-xs font-extrabold uppercase tracking-[0.18em] text-[#2B8A78]">
    <span className="h-[3px] w-8 rounded-full bg-[#2B8A78]" aria-hidden="true" />
    {children}
  </p>
);

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="hero">
        <img src={HERO_IMG} alt="Two young people relaxing in the living room of the home" className="absolute inset-0 h-full w-full object-cover" data-testid="hero-image" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#123329]/95 via-[#123329]/75 to-[#123329]/10" aria-hidden="true" />
        <div className="container-x relative flex min-h-[540px] items-center py-16 sm:min-h-[600px]">
          <div className="max-w-xl">
            <MaskedLines
              as="h1"
              lines={["A child-centred home", "for stability, safety", "and progress"]}
              className="text-4xl font-bold leading-[1.15] text-white sm:text-5xl lg:text-[3.4rem]"
              testid="hero-title"
            />
            <FadeUp delay={0.5}>
              <p className="mt-6 max-w-md text-lg leading-relaxed text-white/85" data-testid="hero-lead">
                A nurturing, consistent and supportive home for one boy, where his individual needs, goals
                and wellbeing come first.
              </p>
            </FadeUp>
            <FadeUp delay={0.65}>
              <div className="mt-9 flex flex-wrap gap-4" data-testid="hero-cta-group">
                <Link
                  to="/referrals"
                  data-testid="hero-cta-referral"
                  className="btn bg-[#2B8A78] text-white hover:-translate-y-0.5 hover:bg-[#237464] hover:shadow-[0_14px_30px_-12px_rgba(43,138,120,0.6)]"
                >
                  Make a Referral
                  <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                </Link>
                <Link
                  to="/team"
                  data-testid="hero-cta-team"
                  className="btn border border-white/60 text-white hover:-translate-y-0.5 hover:bg-white/10"
                >
                  Talk to Our Team
                </Link>
              </div>
            </FadeUp>
          </div>
        </div>
        <img
          src="/logo-mark.png"
          alt=""
          aria-hidden="true"
          className="absolute bottom-6 right-6 hidden h-36 w-auto sm:block"
          data-testid="hero-ribbon"
        />
      </section>

      {/* Three pillars */}
      <section className="container-x py-14 sm:py-16" data-testid="pillars-section">
        <div className="grid gap-6 sm:grid-cols-3">
          {PILLARS.map(({ title, copy, Icon, circle, ring }) => (
            <FadeUp key={title}>
              <div className="group relative h-full overflow-hidden rounded-2xl border border-line bg-white p-7 transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-[0_24px_45px_-28px_rgba(23,55,46,0.45)]" data-testid={`pillar-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="absolute -bottom-10 -right-10 h-28 w-28 rounded-full border-[12px] opacity-25" style={{ borderColor: ring }} aria-hidden="true" />
                <span className="flex h-12 w-12 items-center justify-center rounded-full text-white" style={{ background: circle }}>
                  <Icon className="h-6 w-6" strokeWidth={1.5} />
                </span>
                <h3 className="mt-5 text-xl font-bold text-[#17372E]">{title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-mute">{copy}</p>
                <ArrowRight className="mt-5 h-5 w-5 text-[#17372E] transition-transform duration-200 group-hover:translate-x-1" strokeWidth={1.5} />
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* A place to belong */}
      <section className="container-x pb-16 sm:pb-20" data-testid="belong-section">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <FadeUp>
            <img
              src={BELONG_IMG}
              alt="The front of the home at 81 Bicester Road"
              className="h-[300px] w-full rounded-2xl object-cover sm:h-[420px]"
              data-testid="belong-image"
            />
          </FadeUp>
          <FadeUp delay={0.12}>
            <div className="relative">
              <SectionEyebrow>Our home &amp; our mission</SectionEyebrow>
              <h2 className="mt-4 text-3xl font-bold leading-tight text-[#17372E] sm:text-4xl" data-testid="belong-title">
                A place to belong, with a future to look forward to.
              </h2>
              <p className="mt-6 max-w-lg text-base leading-relaxed text-mute" data-testid="belong-copy">
                Infinity &amp; Beyond Care is developing a small, nurturing home in Aylesbury where care is
                organised around one child's assessed needs, relationships and goals. We provide a safe,
                supportive environment with consistent routines, clear boundaries and a professional team who
                are committed to helping him thrive.
              </p>
              <Link
                to="/our-service"
                data-testid="belong-link"
                className="group mt-8 inline-flex items-center gap-2 rounded-full border-2 border-[#17372E] px-6 py-3 text-sm font-bold text-[#17372E] transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-[#17372E] hover:text-white"
              >
                Learn More About Us
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" strokeWidth={1.5} />
              </Link>
              <img src="/logo-mark.png" alt="" aria-hidden="true" className="absolute -bottom-10 right-0 hidden h-24 w-auto lg:block" />
            </div>
          </FadeUp>
        </div>
      </section>

      {/* What we work towards */}
      <section className="container-x py-16 sm:py-20" data-testid="outcomes-section">
        <div className="flex flex-col gap-12 lg:flex-row lg:items-center lg:justify-between">
          <FadeUp>
            <div className="max-w-md">
              <SectionEyebrow>What we work towards</SectionEyebrow>
              <h2 className="mt-4 text-3xl font-bold leading-tight text-[#17372E] sm:text-4xl" data-testid="outcomes-title">
                Positive outcomes for a brighter tomorrow.
              </h2>
              <p className="mt-4 text-base leading-relaxed text-mute" data-testid="outcomes-copy">
                We focus on five key areas to help the child build a safer, happier and more independent
                future.
              </p>
            </div>
          </FadeUp>
          <StaggerGroup className="flex flex-wrap gap-8 sm:gap-10">
            {OUTCOMES.map(({ label, Icon, circle }) => (
              <StaggerItem key={label}>
                <div className="flex w-24 flex-col items-center gap-3 text-center" data-testid={`outcome-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <span className="flex h-16 w-16 items-center justify-center rounded-full text-white" style={{ background: circle }}>
                    <Icon className="h-7 w-7" strokeWidth={1.5} />
                  </span>
                  <p className="text-sm font-bold leading-snug text-[#17372E]">{label}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>
    </div>
  );
}
